import { useState } from 'react';
import { useFlag } from '@reflag/react-sdk';
import SaveAlt from '@mui/icons-material/SaveAlt';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Menu from 'further-ui/components/Menu';
import {
  downloadFileFromTrpc,
  downloadLinkAsFile,
  pluralize,
  CalendarDay,
} from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import SelectPdfDownloadsModal, {
  type PdfDownloadRow,
} from './SelectPdfDownloadsModal';

type Props = {
  commitmentIds: Array<string>;
  vehicleId?: string;
  investorId: string;
  hasDocuments: boolean;
};

const DownloadMenu: React.FC<Props> = ({
  commitmentIds,
  vehicleId,
  investorId,
  hasDocuments,
}) => {
  const { isEnabled: exportAllDataEnabled } = useFlag(
    'commitment-page-export-all-data',
  );
  const { error } = useNotification();
  const { vehicle } = useLayoutContext();
  const [pdfModalRows, setPdfModalRows] = useState<Array<PdfDownloadRow>>([]);
  const [zipName, setZipName] = useState('');
  const today = new CalendarDay(new Date()).toReadable();

  const resetPdfModal = () => {
    setPdfModalRows([]);
    setZipName('');
  };

  const { mutateAsync: runExport } = api.commitments.export.useMutation(
    useHandleTrpcMutation()({
      invalidations: [],
    }),
  );

  const { mutateAsync: downloadSignedDocuments } =
    api.commitments.downloadSignedDocuments.useMutation(
      useHandleTrpcMutation()({
        invalidations: [],
      }),
    );

  const { data: pdfDownloads, isFetching: isPdfDownloadsFetching } =
    api.commitments.listInvestorStoredPdfDownloads.useQuery(
      {
        vehicleId: vehicleId ?? '',
        investorId: investorId ?? '',
        commitmentIds: commitmentIds ?? [],
      },
      {
        enabled:
          !!vehicleId &&
          !!investorId &&
          Array.isArray(commitmentIds) &&
          commitmentIds.length > 0,
      },
    );

  const capitalActionNotices = pdfDownloads?.capitalActionNotices ?? [];
  const periodEndReports = pdfDownloads?.periodEndReports ?? [];

  const capitalNoticeItemLabel = `Download capital ${pluralize(
    'notice',
    capitalActionNotices.length,
    'notice(s)',
  )}`;
  const investorReportItemLabel = `Download investor ${pluralize(
    'report',
    periodEndReports.length,
    'report(s)',
  )}`;

  const handleClick = (rows: Array<PdfDownloadRow>, documents: string) => {
    if (rows.length === 0) return;
    if (rows.length === 1) {
      const row = rows[0];
      if (!row.previewUrl) return;
      void downloadLinkAsFile(row.previewUrl, row.downloadFileName, (message) =>
        error(message),
      );
      return;
    }
    setPdfModalRows(rows);
    setZipName(`${documents} - ${vehicle?.vehicleName} - ${today}`);
  };

  const handleCapitalNoticesClick = () => {
    handleClick(capitalActionNotices, 'Capital notices');
  };

  const handleInvestorReportsClick = () => {
    handleClick(periodEndReports, 'Investor reports');
  };

  return (
    <>
      <Menu
        handleVariant="square"
        handleSize="small"
        handleIcon={SaveAlt}
        gap="xs"
        alignWithIconRightEdge
        loading={isPdfDownloadsFetching}
        items={[
          {
            label: 'Download subscription documents',
            icon: SaveAlt,
            disabled: !hasDocuments,
            tooltip: hasDocuments
              ? undefined
              : 'There are no documents associated with this commitment.',
            onClick: async () => {
              if (!commitmentIds || !vehicleId) return;
              const data = await downloadSignedDocuments({
                commitmentId: commitmentIds,
                vehicleId,
              });
              downloadFileFromTrpc(data, () =>
                error('No documents available.'),
              );
            },
          },
          ...(capitalActionNotices.length > 0
            ? [
                {
                  label: capitalNoticeItemLabel,
                  icon: SaveAlt,
                  onClick: handleCapitalNoticesClick,
                },
              ]
            : []),
          ...(periodEndReports.length > 0
            ? [
                {
                  label: investorReportItemLabel,
                  icon: SaveAlt,
                  onClick: handleInvestorReportsClick,
                },
              ]
            : []),
          ...(exportAllDataEnabled
            ? [
                {
                  label: 'Export all data',
                  icon: OpenInNew,
                  onClick: async () => {
                    if (!commitmentIds || !vehicleId) return;
                    const data = await runExport({
                      commitmentId: commitmentIds,
                      vehicleId,
                    });
                    downloadFileFromTrpc(data, () =>
                      error('No documents available.'),
                    );
                  },
                },
              ]
            : []),
        ]}
      />
      {pdfModalRows.length > 0 ? (
        <SelectPdfDownloadsModal
          open
          onClose={resetPdfModal}
          onDownloadError={error}
          rows={pdfModalRows}
          zipName={zipName}
        />
      ) : null}
    </>
  );
};

export default DownloadMenu;
